import * as React3 from 'react';
import { Dialog, DialogTrigger, Button, DialogContent, DialogHeader, DialogTitle, DialogDescription, CatalogItemForm, WhyPanel, CatalogFieldOverridesEditor, DialogFooter, cn, ResourceTable, useDebounced, Input, EnumSelect, ChainSelect, AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, Badge, WhyPopover, PrimaryChainBadge, ChainAvatar, useSignedCdnImage, ColorSwatch } from '@authe/ui-core';
import { draftToRowBody, rowToDraft, emptyDraft, overridesDraftFromDto, CATALOG_ITEM_KIND_LABEL, CATALOG_ITEM_KINDS, validateDraft, diffFieldOverrides, deriveCatalogOwnership, PRIMARY_KINDS } from '@authe/fe-commons/catalog';
export { CATALOG_ITEM_KINDS, CATALOG_ITEM_KIND_LABEL, CatalogItemKind, deriveCatalogOwnership, draftToRowBody, emptyDraft, isChainAgnostic, rowToDraft, validateDraft } from '@authe/fe-commons/catalog';
import { pluralize } from '@authe/fe-commons/display';
import { jsx, jsxs } from 'react/jsx-runtime';
import { toStoredChainId, toChainSlug, shortenAddress } from '@authe/fe-commons/chains';
import { unwrapData } from '@authe/fe-commons/api';
import { uploadLogo, assetUrl } from '@authe/fe-commons/assets';
import { createLogger } from '@authe/fe-commons/logging';

// src/CatalogManager.tsx
var CatalogContext = React3.createContext(null);
function CatalogProvider({ api, defaultQuery, children }) {
  const value = React3.useMemo(() => ({ api, defaultQuery }), [api, defaultQuery]);
  return /* @__PURE__ */ jsx(CatalogContext.Provider, { value, children });
}
function useCatalog() {
  const value = React3.useContext(CatalogContext);
  if (!value) throw new Error("useCatalog must be used within a <CatalogProvider>");
  return value;
}
function useOptionalCatalog() {
  return React3.useContext(CatalogContext);
}
function useAsyncResource(loader, deps) {
  const [data, setData] = React3.useState();
  const [isLoading, setIsLoading] = React3.useState(true);
  const [error, setError] = React3.useState(null);
  const [tick, setTick] = React3.useState(0);
  const loaderRef = React3.useRef(loader);
  loaderRef.current = loader;
  React3.useEffect(() => {
    let alive = true;
    setIsLoading(true);
    setError(null);
    loaderRef.current().then((result) => {
      if (alive) {
        setData(result);
        setIsLoading(false);
      }
    }).catch((err) => {
      if (alive) {
        setError(err);
        setIsLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, [...deps, tick]);
  const refetch = React3.useCallback(() => setTick((value) => value + 1), []);
  return { data, isLoading, error, refetch, setData };
}

// src/data/catalogPaging.ts
function nextCursor(first, tail) {
  return tail ? tail.cursor : first?.nextCursor;
}
function hasMore(first, tail) {
  return Boolean(nextCursor(first, tail)) && (tail ? tail.hasMore : Boolean(first?.hasMore));
}
function allRows(first, tail) {
  return [...first?.items ?? [], ...tail?.rows ?? []];
}
function appendPage(prev, page) {
  return {
    rows: [...prev?.rows ?? [], ...page.items],
    cursor: page.nextCursor,
    hasMore: Boolean(page.hasMore)
  };
}
function upsertRow(rows, next) {
  const list = rows ?? [];
  return list.some((r) => r.id === next.id) ? list.map((r) => r.id === next.id ? next : r) : [next, ...list];
}
function tailHolds(tail, id) {
  return (tail?.rows ?? []).some((r) => r.id === id);
}

// src/data/useCatalogItems.ts
function useCatalogItems(kind, query) {
  const { api, defaultQuery } = useCatalog();
  const mergedQuery = React3.useMemo(() => ({ ...defaultQuery, ...query }), [defaultQuery, query]);
  const queryKey = JSON.stringify(mergedQuery);
  const resource = useAsyncResource(() => api.list(kind, mergedQuery), [kind, queryKey]);
  const [tail, setTail] = React3.useState(null);
  const [isLoadingMore, setIsLoadingMore] = React3.useState(false);
  const readKey = `${kind}|${queryKey}`;
  const [lastRead, setLastRead] = React3.useState(readKey);
  if (lastRead !== readKey) {
    setLastRead(readKey);
    setTail(null);
    setIsLoadingMore(false);
  }
  const first = resource.data;
  const cursor = nextCursor(first, tail);
  const more = hasMore(first, tail);
  const loadMore = React3.useCallback(() => {
    if (!cursor || isLoadingMore) return;
    setIsLoadingMore(true);
    void api.list(kind, { ...mergedQuery, cursor }).then((page) => setTail((prev) => appendPage(prev, page))).finally(() => setIsLoadingMore(false));
  }, [api, cursor, isLoadingMore, kind, mergedQuery]);
  const patchTail = React3.useCallback(
    (fn) => setTail((prev) => prev ? { ...prev, rows: fn(prev.rows) } : prev),
    []
  );
  const reconcileChains = React3.useCallback(
    async (id, chains) => {
      if (api.setChains && chains !== void 0)
        await api.setChains(kind, id, chains.map((c) => toStoredChainId(c) ?? c));
    },
    [api, kind]
  );
  const create = React3.useCallback(
    async (draft) => {
      const row = await api.create(kind, draftToRowBody(draft));
      const appliesTo = kind === "address" ? draft.chains : draft.chains ?? [];
      await reconcileChains(row.id, appliesTo);
      const withChains = { ...row, chains: draft.chains ?? row.chains };
      resource.setData((prev) => ({ ...prev, items: [withChains, ...prev?.items ?? []] }));
      return withChains;
    },
    [api, kind, reconcileChains, resource]
  );
  const update = React3.useCallback(
    async (id, draft) => {
      const row = await api.update(kind, id, draftToRowBody(draft));
      await reconcileChains(id, draft.chains);
      const withChains = { ...row, chains: draft.chains ?? row.chains };
      if (tailHolds(tail, id)) patchTail((rows) => rows.map((r) => r.id === id ? withChains : r));
      else resource.setData((prev) => ({ ...prev, items: upsertRow(prev?.items, withChains) }));
      return withChains;
    },
    [api, kind, patchTail, reconcileChains, resource, tail]
  );
  const remove = React3.useCallback(
    async (id) => {
      await api.remove(kind, id);
      resource.setData((prev) => ({
        ...prev,
        items: (prev?.items ?? []).filter((r) => r.id !== id)
      }));
      patchTail((rows) => rows.filter((r) => r.id !== id));
    },
    [api, kind, patchTail, resource]
  );
  const saveChains = React3.useCallback(
    async (id, chains) => {
      await reconcileChains(id, chains);
      const withChains = (r) => r.id === id ? { ...r, chains } : r;
      resource.setData((prev) => ({ ...prev, items: (prev?.items ?? []).map(withChains) }));
      patchTail((rows) => rows.map(withChains));
    },
    [patchTail, reconcileChains, resource]
  );
  const items = React3.useMemo(() => allRows(first, tail), [first, tail]);
  return {
    items,
    isLoading: resource.isLoading,
    error: resource.error,
    refetch: resource.refetch,
    create,
    update,
    remove,
    saveChains,
    hasMore: more,
    loadMore,
    isLoadingMore,
    total: first?.total,
    totalCapped: first?.totalCapped
  };
}
var useLabels = (query) => useCatalogItems("label", query);
var useTags = (query) => useCatalogItems("tag", query);
var useEntities = (query) => useCatalogItems("entity", query);
var useGroups = (query) => useCatalogItems("group", query);
var useAddresses = (query) => useCatalogItems("address", query);
var targetKey = (t) => `${t.scope ?? "address"}:${t.targetId}:${t.chainId ?? "_"}`;
function usePrimaryAssociations(target) {
  const { api } = useCatalog();
  const key = targetKey(target);
  const enabled = typeof api.getPrimaries === "function";
  const resource = useAsyncResource(
    () => api.getPrimaries ? api.getPrimaries(target) : Promise.resolve({}),
    [key, enabled]
  );
  const primaries = resource.data ?? {};
  const isPrimary = React3.useCallback(
    (kind, catalogId) => primaries[kind] === catalogId,
    [primaries]
  );
  const setPrimary = React3.useCallback(
    async (kind, catalogId) => {
      if (!api.setPrimary) return;
      await api.setPrimary(target, kind, catalogId);
      resource.setData((prev) => ({ ...prev ?? {}, [kind]: catalogId }));
    },
    [api, target, resource]
  );
  const clearPrimary = React3.useCallback(
    async (kind) => {
      if (!api.clearPrimary) return;
      await api.clearPrimary(target, kind);
      resource.setData((prev) => {
        const next = { ...prev ?? {} };
        delete next[kind];
        return next;
      });
    },
    [api, target, resource]
  );
  const togglePrimary = React3.useCallback(
    async (kind, catalogId) => {
      if (isPrimary(kind, catalogId)) await clearPrimary(kind);
      else await setPrimary(kind, catalogId);
    },
    [isPrimary, clearPrimary, setPrimary]
  );
  return {
    primaries,
    isLoading: resource.isLoading,
    error: resource.error,
    refetch: resource.refetch,
    enabled,
    isPrimary,
    setPrimary,
    clearPrimary,
    togglePrimary
  };
}
function CatalogItemDialog({
  kind,
  mode = "create",
  row,
  onSubmit,
  trigger,
  open,
  onOpenChange,
  includeTestnets,
  title,
  description
}) {
  const { api } = useCatalog();
  const [internalOpen, setInternalOpen] = React3.useState(false);
  const isControlled = open !== void 0;
  const isOpen = isControlled ? open : internalOpen;
  const setOpen = React3.useCallback(
    (next) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );
  const [draft, setDraft] = React3.useState(
    () => mode === "edit" && row ? rowToDraft(kind, row) : emptyDraft(kind)
  );
  const [errors, setErrors] = React3.useState({});
  const [busy, setBusy] = React3.useState(false);
  const [submitError, setSubmitError] = React3.useState(null);
  const supportsOverrides = kind !== "address" && Boolean(api.getFieldOverrides || api.setFieldOverrides);
  const [overrides, setOverrides] = React3.useState({});
  const initialOverrides = React3.useRef({});
  const toCandidate = React3.useCallback(
    (r) => ({
      id: r.id,
      name: typeof r.name === "string" && r.name ? r.name : r.id,
      color: typeof r.color === "string" ? r.color : void 0,
      logo: typeof r.icon === "string" ? r.icon : typeof r.logo_s3_uri === "string" ? r.logo_s3_uri : void 0
    }),
    []
  );
  const loadGroupCandidates = React3.useCallback(
    async (memberKind, search) => {
      const page = await api.list(memberKind, { search, limit: 20 });
      return page.items.map(toCandidate);
    },
    [api, toCandidate]
  );
  const getByIds = api.getByIds;
  const resolveGroupSelected = React3.useMemo(
    () => getByIds ? async (memberKind, ids) => (await getByIds(memberKind, ids)).map(toCandidate) : void 0,
    [getByIds, toCandidate]
  );
  React3.useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSubmitError(null);
    setOverrides({});
    initialOverrides.current = {};
    if (mode === "edit" && row) {
      const base = rowToDraft(kind, row);
      setDraft({
        ...base,
        chains: row.chains?.map((c) => toChainSlug(c) ?? c)
      });
      let alive = true;
      if (api.getChains) {
        api.getChains(kind, row.id).then(
          (chains) => alive && setDraft(
            (d) => ({ ...d, chains: chains.map((c) => toChainSlug(c) ?? c) })
          )
        ).catch(() => void 0);
      }
      if (supportsOverrides && api.getFieldOverrides) {
        api.getFieldOverrides(kind, row.id).then((map) => {
          if (!alive) return;
          const drafts = {};
          for (const [chain, dto] of Object.entries(map))
            drafts[toChainSlug(chain) ?? chain] = overridesDraftFromDto(dto);
          initialOverrides.current = drafts;
          setOverrides(drafts);
        }).catch(() => void 0);
      }
      return () => {
        alive = false;
      };
    }
    setDraft(emptyDraft(kind));
    return void 0;
  }, [isOpen, mode, kind, row?.id]);
  const onUploadLogo = api.uploadLogo ? async (file) => {
    const id = draft.kind === "address" ? draft.address.trim() || void 0 : row?.id;
    const result = await api.uploadLogo({ kind, id, chain: draftChain(draft), file });
    return { key: result.key, url: result.url };
  } : void 0;
  const submit = async (event) => {
    event.preventDefault();
    const result = validateDraft(draft);
    if (!result.success || !result.data) {
      setErrors(result.fieldErrors);
      return;
    }
    setErrors({});
    setSubmitError(null);
    setBusy(true);
    try {
      const saved = await onSubmit(result.data);
      const id = saved?.id ?? row?.id;
      if (id && supportsOverrides && api.setFieldOverrides) {
        const changes = diffFieldOverrides(initialOverrides.current, overrides);
        await Promise.all(
          changes.map(
            (c) => api.setFieldOverrides(kind, id, toStoredChainId(c.chainId) ?? c.chainId, c.value)
          )
        );
      }
      setOpen(false);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Could not save");
    } finally {
      setBusy(false);
    }
  };
  const kindLabel = CATALOG_ITEM_KIND_LABEL[kind];
  const heading = title ?? `${mode === "edit" ? "Edit" : "New"} ${kindLabel.toLowerCase()}`;
  const overrideBase = {
    name: draft.name,
    description: draft.description,
    color: draft.color,
    logo: draft.logo,
    aliases: draft.aliases
  };
  return /* @__PURE__ */ jsxs(Dialog, { open: isOpen, onOpenChange: setOpen, children: [
    trigger !== void 0 ? /* @__PURE__ */ jsx(DialogTrigger, { asChild: true, children: trigger }) : !isControlled ? /* @__PURE__ */ jsx(DialogTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { type: "button", children: [
      "New ",
      kindLabel.toLowerCase()
    ] }) }) : null,
    /* @__PURE__ */ jsx(DialogContent, { className: "max-h-[85vh] overflow-y-auto sm:max-w-lg", children: /* @__PURE__ */ jsxs("form", { onSubmit: submit, children: [
      /* @__PURE__ */ jsxs(DialogHeader, { children: [
        /* @__PURE__ */ jsx(DialogTitle, { children: heading }),
        description && /* @__PURE__ */ jsx(DialogDescription, { children: description })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "my-4", children: [
        /* @__PURE__ */ jsx(
          CatalogItemForm,
          {
            value: draft,
            onChange: setDraft,
            errors,
            onUploadLogo,
            resolveLogoUrl: api.resolveLogoUrl,
            includeTestnets,
            loadGroupCandidates,
            resolveGroupSelected,
            disabled: busy,
            lockName: mode === "edit" && kind !== "address"
          }
        ),
        mode === "edit" && row && // Glass-box: on an existing row, show *why* it exists (persisted
        // `explanation` bullets + headline confidence + lifecycle, with a
        // read-time fallback from raw `evidence`).
        /* @__PURE__ */ jsx("div", { className: "mt-4 rounded-md border bg-muted/20 p-3", children: /* @__PURE__ */ jsx(
          WhyPanel,
          {
            confidence: typeof row.confidence === "number" ? row.confidence : void 0,
            lifecycle: typeof row.lifecycle === "string" ? row.lifecycle : void 0,
            explanation: Array.isArray(row.explanation) ? row.explanation : void 0,
            evidence: Array.isArray(row.evidence) ? row.evidence : void 0
          }
        ) }),
        supportsOverrides && /* @__PURE__ */ jsxs("details", { className: "mt-4 rounded-md border p-3", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-sm font-medium", children: "Per-chain overrides" }),
          /* @__PURE__ */ jsxs("p", { className: "mb-3 mt-1 text-xs text-muted-foreground", children: [
            "Customise how this ",
            kindLabel.toLowerCase(),
            " appears on specific chains. Blank fields inherit the base values above."
          ] }),
          /* @__PURE__ */ jsx(
            CatalogFieldOverridesEditor,
            {
              value: overrides,
              onChange: setOverrides,
              base: overrideBase,
              includeTestnets,
              disabled: busy,
              resolveLogoUrl: api.resolveLogoUrl,
              onUploadLogo: api.uploadLogo ? async (chain, file) => {
                const r = await api.uploadLogo({ kind, id: row?.id, chain, file });
                return { key: r.key, url: r.url };
              } : void 0
            }
          )
        ] }),
        submitError && /* @__PURE__ */ jsx("p", { className: "mt-3 text-sm text-destructive", children: submitError })
      ] }),
      /* @__PURE__ */ jsxs(DialogFooter, { children: [
        /* @__PURE__ */ jsx(Button, { type: "button", variant: "ghost", onClick: () => setOpen(false), children: "Cancel" }),
        /* @__PURE__ */ jsx(Button, { type: "submit", disabled: busy, children: mode === "edit" ? "Save changes" : `Create ${kindLabel.toLowerCase()}` })
      ] })
    ] }) })
  ] });
}
function draftChain(draft) {
  if (draft.kind === "address" || draft.kind === "label") return draft.chain || void 0;
  return void 0;
}
function StarIcon({ filled, ...props }) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      viewBox: "0 0 24 24",
      fill: filled ? "currentColor" : "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": true,
      ...props,
      children: /* @__PURE__ */ jsx("path", { d: "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" })
    }
  );
}
function PrimaryStar({
  active,
  onToggle,
  disabled,
  label,
  size = "md",
  className
}) {
  const [busy, setBusy] = React3.useState(false);
  const handleClick = React3.useCallback(async () => {
    if (busy || disabled) return;
    setBusy(true);
    try {
      await onToggle();
    } finally {
      setBusy(false);
    }
  }, [busy, disabled, onToggle]);
  const dim = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  const name = label ? ` ${label}` : "";
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      role: "switch",
      "aria-checked": active,
      "aria-label": active ? `Unset primary${name}` : `Set as primary${name}`,
      title: active ? "Primary \u2014 click to unset" : "Set as primary",
      onClick: handleClick,
      disabled: disabled || busy,
      "data-primary": active ? "true" : "false",
      className: cn(
        "inline-flex shrink-0 items-center justify-center rounded-md p-1.5 transition-colors",
        active ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground hover:bg-accent hover:text-foreground",
        (disabled || busy) && "pointer-events-none opacity-50",
        className
      ),
      children: /* @__PURE__ */ jsx(StarIcon, { filled: active, className: dim })
    }
  );
}
var str = (v) => typeof v === "string" && v !== "" ? v : void 0;
var strArr = (v) => Array.isArray(v) ? v.filter((x) => typeof x === "string" && x !== "") : [];
var OWNERSHIP_BADGE = {
  user: "default",
  tenant: "secondary",
  org: "secondary",
  platform: "outline"
};
function isPrimaryCapable(kind) {
  return PRIMARY_KINDS.includes(kind);
}
function isReadOnly(row) {
  return deriveCatalogOwnership(row).readOnly || row.group_type === "transaction";
}
function logoKeyOf(row) {
  return str(row.icon) ?? str(row.logo_s3_uri) ?? str(row.logo);
}
function RowLogo({ logoUrl, color }) {
  const img = useSignedCdnImage(logoUrl);
  if (logoUrl && img.src) {
    return /* @__PURE__ */ jsx(
      "img",
      {
        src: img.src,
        ...img.crossOrigin ? { crossOrigin: img.crossOrigin } : {},
        alt: "",
        className: "h-6 w-6 shrink-0 rounded-md border object-cover",
        style: color ? { borderColor: color } : void 0
      }
    );
  }
  if (color) return /* @__PURE__ */ jsx(ColorSwatch, { color, size: "sm" });
  return /* @__PURE__ */ jsx("span", { className: "inline-block h-5 w-5 shrink-0 rounded-md border bg-muted" });
}
function LockIcon(props) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": true,
      ...props,
      children: [
        /* @__PURE__ */ jsx("rect", { x: "3", y: "11", width: "18", height: "11", rx: "2" }),
        /* @__PURE__ */ jsx("path", { d: "M7 11V7a5 5 0 0 1 10 0v4" })
      ]
    }
  );
}
function PencilIcon(props) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": true,
      ...props,
      children: [
        /* @__PURE__ */ jsx("path", { d: "M12 20h9" }),
        /* @__PURE__ */ jsx("path", { d: "M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" })
      ]
    }
  );
}
function TrashIcon(props) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": true,
      ...props,
      children: [
        /* @__PURE__ */ jsx("path", { d: "M3 6h18" }),
        /* @__PURE__ */ jsx("path", { d: "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" })
      ]
    }
  );
}
function createCatalogColumns({
  kind,
  onEdit,
  onDelete,
  resolveLogoUrl,
  showOwnership = true,
  primary
}) {
  const showPrimary = primary?.enabled === true && isPrimaryCapable(kind);
  const columns = [];
  if (showPrimary && isPrimaryCapable(kind)) {
    columns.push({
      id: "primary",
      header: "",
      size: 44,
      enableHiding: false,
      enableSorting: false,
      cell: ({ row }) => /* @__PURE__ */ jsx(
        PrimaryStar,
        {
          size: "sm",
          active: primary.isPrimary(kind, row.original.id),
          onToggle: () => primary.togglePrimary(kind, row.original.id),
          label: str(row.original.name) ?? row.original.id
        }
      )
    });
  }
  columns.push({
    id: "name",
    // Sorted and searched on the NAME, not the whole identity cell: the badges
    // beside it are facts about the row, and a reader typing "verified" means
    // the lifecycle filter, not a name containing the word.
    accessorFn: (row) => str(row.name) ?? row.id,
    header: "Name",
    enableHiding: false,
    cell: ({ row }) => {
      const data = row.original;
      const name = str(data.name) ?? data.id;
      const logoKey = logoKeyOf(data);
      const description = str(data.description);
      const lifecycle = str(data.lifecycle);
      const boundAddress = str(data.address);
      const isTxGroup = data.group_type === "transaction";
      const sagaKind = str(data.saga_kind);
      const legCount = typeof data.member_count === "number" ? data.member_count : void 0;
      const sagaState = str(data.state);
      return /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-3", children: [
        /* @__PURE__ */ jsx(
          RowLogo,
          {
            logoUrl: resolveLogoUrl && logoKey ? resolveLogoUrl(logoKey) : void 0,
            color: str(data.color)
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx("span", { className: "truncate text-sm font-medium", children: name }),
            boundAddress && /* @__PURE__ */ jsx(
              "span",
              {
                title: `Bound to ${boundAddress} \u2014 this ${kind} describes that address, and cannot be attached to a transaction.`,
                className: "shrink-0 rounded border border-dashed px-1 py-px font-mono text-[10px] text-muted-foreground",
                children: shortenAddress(boundAddress)
              }
            ),
            isTxGroup && /* @__PURE__ */ jsx(
              Badge,
              {
                variant: "outline",
                className: "shrink-0 border-brand/40 text-[10px] text-brand",
                title: `Found automatically by the cross-chain grouping engine` + (sagaKind ? ` (${sagaKind})` : "") + `. Curated groups are the ones your team named.`,
                children: "Cross-chain"
              }
            ),
            isTxGroup && legCount != null && /* @__PURE__ */ jsxs("span", { className: "shrink-0 text-xs text-muted-foreground", children: [
              legCount,
              " ",
              legCount === 1 ? "leg" : "legs"
            ] }),
            isTxGroup && sagaState && /* @__PURE__ */ jsx(
              Badge,
              {
                variant: sagaState === "completed" ? "secondary" : "outline",
                className: "shrink-0 text-[10px] capitalize",
                title: sagaState === "completed" ? "Every leg of this saga has landed." : "Still waiting on a leg \u2014 the destination transaction hasn't been seen yet.",
                children: sagaState
              }
            ),
            /* @__PURE__ */ jsx(
              WhyPopover,
              {
                confidence: typeof data.confidence === "number" ? data.confidence : void 0,
                lifecycle,
                explanation: Array.isArray(data.explanation) ? data.explanation : void 0,
                evidence: Array.isArray(data.evidence) ? data.evidence : void 0
              }
            )
          ] }),
          description && /* @__PURE__ */ jsx("p", { className: "truncate text-xs text-muted-foreground", children: description })
        ] })
      ] });
    }
  });
  columns.push({
    id: "lifecycle",
    accessorFn: (row) => str(row.lifecycle) ?? "",
    header: "Status",
    size: 120,
    cell: ({ row }) => {
      const lifecycle = str(row.original.lifecycle);
      if (!lifecycle) return /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2014" });
      return /* @__PURE__ */ jsx(Badge, { variant: lifecycle === "verified" ? "default" : "secondary", className: "text-[10px]", children: lifecycle });
    }
  });
  if (showOwnership) {
    columns.push({
      id: "ownership",
      // The label, not the scope token, because it is what the facet lists and
      // what the cell shows — a filter offering "platform" beside a cell reading
      // "Platform default" is two names for one thing.
      accessorFn: (row) => deriveCatalogOwnership(row).label,
      header: "Source",
      size: 140,
      cell: ({ row }) => {
        const ownership = deriveCatalogOwnership(row.original);
        return /* @__PURE__ */ jsx(Badge, { variant: OWNERSHIP_BADGE[ownership.scope], className: "text-[10px]", children: ownership.label });
      }
    });
  }
  columns.push({
    id: "chains",
    accessorFn: (row) => (row.chains ?? []).join(","),
    header: "Chains",
    size: 180,
    enableSorting: false,
    cell: ({ row }) => {
      const data = row.original;
      const chains = data.chains ?? [];
      const primaryChains = strArr(data.primary_chains);
      const primarySet = new Set(primaryChains);
      const shown = chains.length ? [...chains].sort((a, b) => Number(primarySet.has(b)) - Number(primarySet.has(a))) : primaryChains;
      return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
        primaryChains.length > 0 && /* @__PURE__ */ jsx(PrimaryChainBadge, { primary: true, className: "mr-0.5" }),
        chains.length === 0 && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "All chains" }),
        shown.slice(0, 4).map((c) => /* @__PURE__ */ jsx(
          ChainAvatar,
          {
            chain: c,
            size: "sm",
            showName: false,
            className: cn(primarySet.has(c) && "rounded-full ring-2 ring-amber-500/70")
          },
          c
        )),
        shown.length > 4 && /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground", children: [
          "+",
          shown.length - 4
        ] })
      ] });
    }
  });
  columns.push({
    id: "actions",
    header: "",
    size: 88,
    enableHiding: false,
    enableSorting: false,
    cell: ({ row }) => {
      const data = row.original;
      const name = str(data.name) ?? data.id;
      const isTxGroup = data.group_type === "transaction";
      if (isReadOnly(data)) {
        return /* @__PURE__ */ jsx(
          "span",
          {
            className: "inline-flex items-center p-1.5 text-muted-foreground",
            title: isTxGroup ? "Detected automatically \u2014 maintained by the grouping engine, not editable here" : "System default \u2014 read-only",
            "aria-label": isTxGroup ? `${name} is maintained by the grouping engine` : `${name} is a read-only system default`,
            children: /* @__PURE__ */ jsx(LockIcon, { className: "h-4 w-4" })
          }
        );
      }
      return /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-0.5", children: [
        onEdit && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-label": `Edit ${name}`,
            onClick: (e) => {
              e.stopPropagation();
              onEdit(data);
            },
            className: "rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground",
            children: /* @__PURE__ */ jsx(PencilIcon, { className: "h-4 w-4" })
          }
        ),
        onDelete && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-label": `Delete ${name}`,
            onClick: (e) => {
              e.stopPropagation();
              onDelete(data);
            },
            className: "rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive",
            children: /* @__PURE__ */ jsx(TrashIcon, { className: "h-4 w-4" })
          }
        )
      ] });
    }
  });
  return columns;
}
function countLabel(kind, loaded, total, capped) {
  const noun = pluralize(kind);
  if (total == null || total <= loaded) return `${loaded.toLocaleString()} ${noun}`;
  return `${loaded.toLocaleString()} of ${total.toLocaleString()}${capped ? "+" : ""} ${noun}`;
}
function CatalogList({
  kind,
  items,
  isLoading,
  error,
  onRetry,
  onEdit,
  onDelete,
  onOpen,
  resolveLogoUrl,
  filtered = false,
  showOwnership = true,
  primary,
  emptyAction,
  hasMore: hasMore2 = false,
  onLoadMore,
  isLoadingMore = false,
  total,
  totalCapped,
  className
}) {
  const columns = React3.useMemo(
    () => createCatalogColumns({ kind, onEdit, onDelete, resolveLogoUrl, showOwnership, primary }),
    [kind, onEdit, onDelete, resolveLogoUrl, showOwnership, primary]
  );
  return /* @__PURE__ */ jsxs("div", { className, children: [
    /* @__PURE__ */ jsx(
      ResourceTable,
      {
        rows: items,
        columns,
        getRowId: (row) => row.id,
        isLoading,
        error,
        onRetry,
        onRowClick: onOpen,
        toolbar: {
          searchColumn: "name",
          searchPlaceholder: `Search ${pluralize(kind)}\u2026`,
          columnLabels: {
            name: "Name",
            lifecycle: "Status",
            ownership: "Source",
            chains: "Chains"
          }
        },
        viewKey: `catalog:${kind}`,
        rowHeight: 56,
        height: "h-[560px]",
        emptyTitle: filtered ? `No ${pluralize(kind)} match` : `No ${pluralize(kind)} yet`,
        emptyDescription: filtered ? "Try a different term, or widen the chain and status filters." : `Create your first ${kind} to get started.`,
        emptyAction: filtered ? void 0 : emptyAction
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: countLabel(kind, items.length, total, totalCapped) }),
      hasMore2 && onLoadMore && /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          variant: "outline",
          size: "sm",
          onClick: onLoadMore,
          disabled: isLoadingMore,
          children: isLoadingMore ? "Loading\u2026" : "Load more"
        }
      )
    ] })
  ] });
}
var GROUP_TYPE_OPTIONS = [
  { value: "", label: "All groups" },
  { value: "transaction", label: "Cross-chain (auto)" },
  { value: "catalog", label: "Curated" }
];
var LIFECYCLE_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "verified", label: "Verified" },
  { value: "candidate", label: "Candidate" },
  { value: "deprecated", label: "Deprecated" },
  { value: "quarantined", label: "Quarantined" }
];
function CatalogFilterBar({
  value,
  onChange,
  subject,
  showGroupType = false,
  includeTestnets,
  className
}) {
  const [text, setText] = React3.useState(value.search ?? "");
  const settled = useDebounced(text.trim());
  const reported = React3.useRef(value.search ?? "");
  React3.useEffect(() => {
    if (settled === reported.current) return;
    reported.current = settled;
    onChange({ ...value, search: settled || void 0 });
  }, [settled]);
  const active = Boolean(value.search || value.chain || value.lifecycle || value.groupType);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-wrap items-center gap-2", className), children: [
    /* @__PURE__ */ jsx(
      Input,
      {
        value: text,
        onChange: (e) => setText(e.target.value),
        placeholder: `Search ${subject}\u2026`,
        "aria-label": `Search ${subject}`,
        className: "h-8 w-full max-w-xs text-sm"
      }
    ),
    showGroupType && /* @__PURE__ */ jsx(
      EnumSelect,
      {
        value: value.groupType ?? "",
        onChange: (groupType) => onChange({ ...value, groupType: groupType || void 0 }),
        options: GROUP_TYPE_OPTIONS,
        placeholder: "All groups",
        className: "h-8 w-[12rem] text-sm"
      }
    ),
    /* @__PURE__ */ jsx(
      ChainSelect,
      {
        value: value.chain ?? "",
        onChange: (chain) => onChange({ ...value, chain: chain || void 0 }),
        allowAny: true,
        anyLabel: "Any chain",
        includeTestnets,
        className: "h-8 w-[11rem] text-sm"
      }
    ),
    /* @__PURE__ */ jsx(
      EnumSelect,
      {
        value: value.lifecycle ?? "",
        onChange: (lifecycle) => onChange({ ...value, lifecycle: lifecycle || void 0 }),
        options: LIFECYCLE_OPTIONS,
        placeholder: "Any status",
        className: "h-8 w-[10rem] text-sm"
      }
    ),
    active && /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        size: "sm",
        variant: "ghost",
        onClick: () => {
          setText("");
          reported.current = "";
          onChange({});
        },
        children: "Clear"
      }
    )
  ] });
}
function withFilters(base, filters) {
  const merged = { ...base };
  if (filters.search) merged.search = filters.search;
  if (filters.chain) merged.chain = filters.chain;
  if (filters.lifecycle) merged.lifecycle = filters.lifecycle;
  if (filters.groupType) merged.groupType = filters.groupType;
  return Object.keys(merged).length > 0 ? merged : void 0;
}
function CatalogManager(props) {
  const { api, ...rest } = props;
  const inner = /* @__PURE__ */ jsx(CatalogManagerInner, { ...rest });
  return api ? /* @__PURE__ */ jsx(CatalogProvider, { api, children: inner }) : inner;
}
function CatalogManagerInner({
  kinds = [...CATALOG_ITEM_KINDS],
  defaultKind,
  query,
  includeTestnets,
  confirmDelete = true,
  showFilters = true,
  kindHints,
  primaryTarget,
  onOpenItem,
  className
}) {
  const [selected, setSelected] = React3.useState(defaultKind ?? kinds[0]);
  const active = kinds.includes(selected) ? selected : defaultKind ?? kinds[0];
  const kindHint = kindHints?.[active];
  const [filters, setFilters] = React3.useState({});
  const effective = React3.useMemo(
    () => active === "group" ? filters : { ...filters, groupType: void 0 },
    [active, filters]
  );
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-4", className), children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1 border-b", children: kinds.map((kind) => /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: () => setSelected(kind),
        className: cn(
          "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
          active === kind ? "border-brand text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
        ),
        children: pluralize(CATALOG_ITEM_KIND_LABEL[kind])
      },
      kind
    )) }),
    showFilters && /* @__PURE__ */ jsx(
      CatalogFilterBar,
      {
        value: effective,
        onChange: setFilters,
        subject: pluralize(CATALOG_ITEM_KIND_LABEL[active]).toLowerCase(),
        showGroupType: active === "group",
        includeTestnets
      }
    ),
    kindHint ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: kindHint }) : null,
    /* @__PURE__ */ jsx(
      CatalogKindPanel,
      {
        kind: active,
        query: withFilters(query, effective),
        filtered: Boolean(
          effective.search || effective.chain || effective.lifecycle || effective.groupType
        ),
        includeTestnets,
        confirmDelete,
        primaryTarget,
        onOpenItem
      },
      active
    )
  ] });
}
function CatalogKindPanel({
  kind,
  query,
  filtered = false,
  includeTestnets,
  confirmDelete,
  primaryTarget,
  onOpenItem
}) {
  const { api } = useCatalog();
  const items = useCatalogItems(kind, query);
  const [editRow, setEditRow] = React3.useState(null);
  const [pendingDelete, setPendingDelete] = React3.useState(null);
  const label = CATALOG_ITEM_KIND_LABEL[kind].toLowerCase();
  const handleDelete = (row) => {
    if (confirmDelete) {
      setPendingDelete(row);
      return;
    }
    void items.remove(row.id);
  };
  const listProps = {
    kind,
    items: items.items,
    isLoading: items.isLoading,
    error: items.error,
    filtered,
    onRetry: items.refetch,
    onEdit: setEditRow,
    onDelete: handleDelete,
    onOpen: onOpenItem ? (row) => onOpenItem(kind, row) : void 0,
    resolveLogoUrl: api.resolveLogoUrl,
    hasMore: items.hasMore,
    onLoadMore: items.loadMore,
    isLoadingMore: items.isLoadingMore,
    total: items.total,
    totalCapped: items.totalCapped
  };
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-sm font-semibold capitalize", children: pluralize(label) }),
      /* @__PURE__ */ jsx(
        CatalogItemDialog,
        {
          kind,
          mode: "create",
          includeTestnets,
          onSubmit: (draft) => items.create(draft),
          trigger: /* @__PURE__ */ jsxs(Button, { type: "button", size: "sm", children: [
            "New ",
            label
          ] })
        }
      )
    ] }),
    primaryTarget ? /* @__PURE__ */ jsx(PrimaryAwareCatalogList, { target: primaryTarget, ...listProps }) : /* @__PURE__ */ jsx(CatalogList, { ...listProps }),
    editRow && /* @__PURE__ */ jsx(
      CatalogItemDialog,
      {
        kind,
        mode: "edit",
        row: editRow,
        open: true,
        includeTestnets,
        onOpenChange: (open) => !open && setEditRow(null),
        onSubmit: (draft) => items.update(editRow.id, draft)
      }
    ),
    /* @__PURE__ */ jsx(
      AlertDialog,
      {
        open: pendingDelete != null,
        onOpenChange: (open) => !open && setPendingDelete(null),
        children: /* @__PURE__ */ jsxs(AlertDialogContent, { children: [
          /* @__PURE__ */ jsxs(AlertDialogHeader, { children: [
            /* @__PURE__ */ jsxs(AlertDialogTitle, { children: [
              "Delete ",
              label,
              " \u201C",
              rowName(pendingDelete),
              "\u201D?"
            ] }),
            /* @__PURE__ */ jsxs(AlertDialogDescription, { children: [
              "This removes the ",
              label,
              " from the catalog and detaches it from every address and transaction it was attached to. Attribution recorded through it \u2014 including who asserted it and why \u2014 goes with it."
            ] })
          ] }),
          /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [
            /* @__PURE__ */ jsx(AlertDialogCancel, { children: "Cancel" }),
            /* @__PURE__ */ jsxs(
              AlertDialogAction,
              {
                onClick: () => {
                  const row = pendingDelete;
                  setPendingDelete(null);
                  if (row) void items.remove(row.id);
                },
                children: [
                  "Delete ",
                  label
                ]
              }
            )
          ] })
        ] })
      }
    )
  ] });
}
function rowName(row) {
  if (!row) return "";
  return typeof row.name === "string" && row.name !== "" ? row.name : row.id;
}
function PrimaryAwareCatalogList({
  target,
  ...listProps
}) {
  const primary = usePrimaryAssociations(target);
  return /* @__PURE__ */ jsx(CatalogList, { ...listProps, primary });
}
function asArray(data) {
  if (Array.isArray(data)) return data;
  return data?.items ?? [];
}
function normalizeRow(row) {
  if (row == null || Array.isArray(row.chains)) return row;
  const appliesTo = row.applies_to_chains;
  return Array.isArray(appliesTo) ? { ...row, chains: appliesTo } : row;
}
var KIND_RESOURCE = {
  entity: "entities",
  tag: "tags",
  label: "labels",
  group: "groups",
  address: "addresses"
};
function createApiCatalogApi(client, options = {}) {
  const base = options.basePath ?? "/catalog";
  const resourceOf = (kind) => KIND_RESOURCE[kind] ?? kind;
  const kindPath = (kind) => `${base}/${resourceOf(kind)}`;
  const rowPath = (kind, id) => `${base}/${resourceOf(kind)}/${encodeURIComponent(id)}`;
  const assignmentsPath = `${base}-assignments`;
  return {
    async list(kind, query = {}) {
      const res = await client.get(kindPath(kind), {
        query: {
          search: query.search,
          chain: query.chain,
          lifecycle: query.lifecycle,
          group_type: query.groupType,
          saga_kind: query.sagaKind,
          limit: query.limit,
          cursor: query.cursor
        }
      });
      const body = unwrapData(res);
      const envelope = Array.isArray(body) ? {} : body ?? {};
      return {
        items: asArray(body).map(normalizeRow),
        nextCursor: envelope.next_cursor ?? void 0,
        hasMore: envelope.has_more ?? void 0,
        total: envelope.approx_total ?? void 0,
        totalCapped: envelope.approx_total_capped ?? void 0
      };
    },
    async create(kind, body) {
      return normalizeRow(unwrapData(await client.post(kindPath(kind), body)));
    },
    async update(kind, id, body) {
      return normalizeRow(unwrapData(await client.put(rowPath(kind, id), body)));
    },
    async remove(kind, id) {
      unwrapData(await client.delete(rowPath(kind, id)));
    },
    async getByIds(kind, ids) {
      if (ids.length === 0) return [];
      const res = await client.get(kindPath(kind), {
        // Comma-JOINED, and not handed over as an array. `buildQueryString` repeats
        // the key per item for arrays, while this route reads a single
        // comma-separated `ids` (its swagger says so) — so a batch of three asked
        // `?ids=a&ids=b&ids=c` and the handler saw only `c`. Every chip but the last
        // then fell back to its raw id, which is what a reader saw on the page:
        // "Crypto" beside `label_c11a…df7b`. Safe to join because a catalog id is
        // `{kind}_{hex}` and cannot contain a comma.
        query: { ids: ids.join(",") }
      });
      return asArray(unwrapData(res)).map(normalizeRow);
    },
    // ── Reverse view (catalog row → resources) ────────────────────────────────
    async listAssignments(query) {
      const res = await client.get(assignmentsPath, {
        query: {
          catalog_kind: query.catalogKind,
          catalog_id: query.catalogId,
          chain: query.chain,
          target_scope: query.targetScope,
          limit: query.limit,
          cursor: query.cursor
        }
      });
      const data = unwrapData(res) ?? {};
      return {
        assignments: data.assignments ?? [],
        // The reverse lane answers a BOUNDED `approx_total`; `total` is the older
        // name, kept as a fallback so a lagging deploy still shows a count.
        total: data.approx_total ?? data.total ?? void 0,
        totalCapped: data.approx_total_capped ?? false,
        nextCursor: data.next_cursor ?? void 0,
        hasMore: data.has_more ?? false
      };
    },
    // ── Chain applicability ──────────────────────────────────────────────────
    async getChains(kind, id) {
      return unwrapData(await client.get(`${rowPath(kind, id)}/chains`));
    },
    async setChains(kind, id, chains) {
      unwrapData(await client.put(`${rowPath(kind, id)}/chains`, { chains }));
    },
    // ── Per-chain field overrides ────────────────────────────────────────────
    async getFieldOverrides(kind, id) {
      return unwrapData(
        await client.get(`${rowPath(kind, id)}/field-overrides`)
      );
    },
    async setFieldOverrides(kind, id, chainId, overrides) {
      unwrapData(
        await client.put(`${rowPath(kind, id)}/field-overrides/${encodeURIComponent(chainId)}`, {
          overrides
        })
      );
    },
    // ── Primary association ──────────────────────────────────────────────────
    async getPrimaries(target) {
      return unwrapData(
        await client.get(`${base}/primaries`, {
          query: { scope: target.scope, targetId: target.targetId, chainId: target.chainId }
        })
      );
    },
    async setPrimary(target, kind, catalogId) {
      unwrapData(
        await client.put(`${base}/primaries`, {
          scope: target.scope,
          targetId: target.targetId,
          chainId: target.chainId,
          kind,
          catalogId
        })
      );
    },
    async clearPrimary(target, kind) {
      unwrapData(
        await client.delete(`${base}/primaries`, {
          query: { scope: target.scope, targetId: target.targetId, chainId: target.chainId, kind }
        })
      );
    },
    // ── Logo (host-provided) ─────────────────────────────────────────────────
    ...options.uploadLogo ? { uploadLogo: options.uploadLogo } : {},
    ...options.resolveLogoUrl ? { resolveLogoUrl: options.resolveLogoUrl } : {}
  };
}
var isAbsolute = (logo) => /^(https?:)?\/\//i.test(logo) || logo.startsWith("data:") || logo.startsWith("blob:");
function provisionalId() {
  return `new-${Math.random().toString(36).slice(2, 10)}`;
}
function createCatalogLogoAdapter(options) {
  const { client, cdnBase, validate, ticketPath } = options;
  return {
    async uploadLogo(input) {
      const result = await uploadLogo(
        client,
        { kind: input.kind, id: input.id ?? provisionalId(), chain: input.chain, file: input.file },
        { validate, path: ticketPath }
      );
      return {
        key: result.key,
        url: result.cdnUrl ?? (result.key ? safeAssetUrl(result.key, cdnBase) : void 0)
      };
    },
    resolveLogoUrl(logo) {
      if (!logo) return void 0;
      if (isAbsolute(logo)) return logo;
      return safeAssetUrl(logo, cdnBase);
    }
  };
}
function safeAssetUrl(key, cdnBase) {
  try {
    return assetUrl(key, { cdnBase });
  } catch {
    return void 0;
  }
}
var CATALOG_PACKAGE = "catalog";
var log = createLogger(`fe:${CATALOG_PACKAGE}`);

// src/components/util.ts
function useAction(fn) {
  const [busy, setBusy] = React3.useState(false);
  const fnRef = React3.useRef(fn);
  fnRef.current = fn;
  const run = React3.useCallback((...args) => {
    const current = fnRef.current;
    if (!current) return;
    let result;
    try {
      result = current(...args);
    } catch (error) {
      log.error("catalog action failed", { error: String(error) });
      return;
    }
    if (result instanceof Promise) {
      setBusy(true);
      result.catch((error) => log.error("catalog action failed", { error: String(error) })).finally(() => setBusy(false));
    }
  }, []);
  return [run, busy];
}

export { CATALOG_PACKAGE, CatalogFilterBar, CatalogItemDialog, CatalogList, CatalogManager, CatalogProvider, PrimaryStar, createApiCatalogApi, createCatalogLogoAdapter, str, useAction, useAddresses, useAsyncResource, useCatalog, useCatalogItems, useEntities, useGroups, useLabels, useOptionalCatalog, usePrimaryAssociations, useTags, withFilters };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map